import React, { useState, useEffect } from "react";
import { editorViewOf } from "utils/mediaEditorView";
import axios from "utils/axios";
import { FaPlus } from "react-icons/fa6";
import { FaFileUpload, FaArrowLeft } from "react-icons/fa";
import { FiFolderPlus } from "react-icons/fi";
import { HiChevronRight } from "react-icons/hi2";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import { showToast } from "components/ui/toast";
import { useParams } from "react-router-dom";
import { isValidUrl } from "utils/helperFunctions";
import PageLoader from "components/ui/PageLoader";
import { useDownloadManager } from "hooks/useDownloadManager";
import DownloadProgressToast from "components/ui/DownloadProgressToast";
import ImageContainer from "components/upload/ImageContainer";
import Lightbox from "components/ui/Lightbox";

const extractS3KeyFromUrl = (url) => {
  if (!url) return null;
  const match = url.match(/s3\.ap-southeast-2\.amazonaws\.com\/(.+)/);
  return match ? match[1] : null;
};

// ═══════════════════════════════════════════════════════════════════
//  ADMIN IMAGE UPLOAD
//  Uploads admin extras (web + print folders) with soft-delete
//  slot replacement and e1/e2/e3 ID naming.
// ═══════════════════════════════════════════════════════════════════

const AdminImageUpload = ({
  address,
  propertyId,
  adminWebPhotos,
  adminPrintPhotos,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [images, setImages] = useState([
    ...adminWebPhotos,
    ...adminPrintPhotos,
  ]);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [uploadProgress, setUploadProgress] = useState({});
  const [activeFolder, setActiveFolder] = useState(null);
  const [editConfirm, setEditConfirm] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);

  const { downloads, isDownloading, queueDownload, removeDownload } =
    useDownloadManager();

  const [folderStatus, setFolderStatus] = useState({
    web: false,
    print: false,
  });

  useEffect(() => {
    const webImages = images.filter((img) => img.folder === "web");
    const printImages = images.filter((img) => img.folder === "print");
    setFolderStatus({
      web:
        webImages.length > 0 &&
        webImages.every(
          (img) => img.isUploaded === undefined || img.isUploaded === true
        ),
      print:
        printImages.length > 0 &&
        printImages.every(
          (img) => img.isUploaded === undefined || img.isUploaded === true
        ),
    });
  }, [images]);

  // ─── Upload logic ───

  const validateFileSize = (file) => {
    const fileSizeInMB = file.size / (1024 * 1024);
    if (activeFolder === "web") {
      if (fileSizeInMB > 1) {
        showToast(
          "error",
          `Web images must be less than 1MB. "${file.name}" is ${fileSizeInMB.toFixed(2)}MB.`
        );
        return false;
      }
    } else {
      if (fileSizeInMB < 5) {
        showToast(
          "error",
          `Print images must be at least 5MB. "${file.name}" is only ${fileSizeInMB.toFixed(2)}MB.`
        );
        return false;
      }
    }
    return true;
  };

  const processFileUpload = async (file, id) => {
    try {
      const res = await axios.post("/admin/prepareMarketing/fileUpload", {
        files: [{ fileName: file.name, fileType: file.type }],
        propertyId,
        folderPath:
          activeFolder === "web" ? "adminWebPhotos" : "adminPrintPhotos",
      });
      const presigned = res.data.presignedUrls[0];
      await axios.put(presigned.url, file, {
        headers: { "Content-Type": file.type },
        onUploadProgress: (e) => {
          setUploadProgress((prev) => ({
            ...prev,
            [id]: Math.round((e.loaded * 100) / e.total),
          }));
        },
      });
      setImages((prev) =>
        prev.map((img) =>
          img.id === id && img.folder === activeFolder
            ? { ...img, isUploaded: true, s3Key: presigned.key }
            : img
        )
      );
    } catch (error) {
      console.error(`Error uploading file ${file.name}:`, error);
      setImages((prev) =>
        prev.map((img) =>
          img.id === id && img.folder === activeFolder
            ? { ...img, isUploaded: false, uploadError: true }
            : img
        )
      );
    }
  };

  const getNextEId = (currentImages, folder) => {
    const folderImages = currentImages.filter(
      (img) => img.folder === folder && !img.isDeleted
    );
    const existingEIds = folderImages
      .filter((img) => typeof img.id === "string" && img.id.startsWith("e"))
      .map((img) => parseInt(img.id.replace("e", ""), 10))
      .filter((num) => !isNaN(num));
    return (existingEIds.length > 0 ? Math.max(...existingEIds) : 0) + 1;
  };

  const getAvailableSlots = (currentImages, folder) => {
    return currentImages
      .filter((img) => img.folder === folder && img.isDeleted)
      .map((img) => img.id);
  };

  const handleFileUpload = async (event, targetId) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    setIsUploading(true);

    if (targetId) {
      // Single file — replacing a deleted slot
      const file = files[0];
      if (!validateFileSize(file)) {
        event.target.value = "";
        setIsUploading(false);
        return;
      }
      const mappedFile = {
        id: targetId,
        file,
        url: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
        folder: activeFolder,
        isUploaded: false,
        isDeleted: false,
      };
      setImages((prev) =>
        prev.map((img) =>
          img.id === targetId && img.folder === activeFolder ? mappedFile : img
        )
      );
      event.target.value = "";
      await processFileUpload(file, targetId);
    } else {
      // Multiple files — fill slots then add new
      const validFiles = files.filter((f) => validateFileSize(f));
      if (validFiles.length === 0) {
        event.target.value = "";
        setIsUploading(false);
        return;
      }
      const availableSlots = getAvailableSlots(images, activeFolder);
      const mappedFiles = [];
      let nextId = getNextEId(images, activeFolder);
      validFiles.forEach((file, index) => {
        const newId =
          index < availableSlots.length
            ? availableSlots[index]
            : `e${nextId++}`;
        mappedFiles.push({
          id: newId,
          file,
          url: URL.createObjectURL(file),
          name: file.name,
          size: file.size,
          folder: activeFolder,
          isUploaded: false,
          isDeleted: false,
        });
      });
      setImages((prev) => {
        let updated = [...prev];
        mappedFiles.forEach((mf) => {
          const idx = updated.findIndex(
            (img) => img.id === mf.id && img.folder === activeFolder
          );
          if (idx !== -1) updated[idx] = mf;
          else updated.push(mf);
        });
        return updated;
      });
      event.target.value = "";
      await Promise.all(
        mappedFiles.map((mf) => processFileUpload(mf.file, mf.id))
      );
    }

    setIsUploading(false);
  };

  // ─── Delete logic (soft-delete) ───

  const deleteImage = (imageId, folder) => {
    setImages((prev) =>
      prev.map((img) =>
        img.id === imageId && img.folder === folder
          ? { ...img, isDeleted: true, url: null }
          : img
      )
    );
  };

  const deleteAllImages = () => {
    images.forEach((img) => {
      if (
        img.url &&
        img.url.startsWith("blob:") &&
        img.folder === activeFolder
      ) {
        URL.revokeObjectURL(img.url);
      }
    });
    setImages((prev) => prev.filter((img) => img.folder !== activeFolder));
  };

  // ─── Save ───

  const sendToReview = async () => {
    try {
      setReviewLoading(true);
      const getIdNumber = (id) => {
        if (typeof id === "string" && id.startsWith("e"))
          return parseInt(id.replace("e", ""), 10) || 0;
        return 0;
      };
      const webImages = images
        .filter(
          (img) =>
            img.folder === "web" &&
            !img.isDeleted &&
            (img.isUploaded || img.url)
        )
        .sort((a, b) => getIdNumber(a.id) - getIdNumber(b.id));
      const printImages = images
        .filter(
          (img) =>
            img.folder === "print" &&
            !img.isDeleted &&
            (img.isUploaded || img.url)
        )
        .sort((a, b) => getIdNumber(a.id) - getIdNumber(b.id));
      const webKeys = webImages.map((img, i) => ({
        id: `e${i + 1}`,
        name: img.name,
        size: img.size,
        s3Key: img.s3Key || extractS3KeyFromUrl(img.url),
      }));
      const printKeys = printImages.map((img, i) => ({
        id: `e${i + 1}`,
        name: img.name,
        size: img.size,
        s3Key: img.s3Key || extractS3KeyFromUrl(img.url),
      }));
      const res = await axios.put(`/admin/prepareMarketing/${propertyId}`, {
        adminWebPhotos: webKeys,
        adminPrintPhotos: printKeys,
      });
      if (res.data.success) {
        setEditConfirm(false);
        window.location.reload();
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setReviewLoading(false);
    }
  };

  // ─── Helpers ───

  const currentFolderImages = images.filter(
    (img) => img.folder === activeFolder
  );
  const visibleImages = currentFolderImages.filter(
    (img) => !img.isDeleted && img.url
  );

  const handleValidateAndSave = () => {
    const webCount = images.filter(
      (img) =>
        (img.isUploaded || img.url) && !img.isDeleted && img.folder === "web"
    ).length;
    const printCount = images.filter(
      (img) =>
        (img.isUploaded || img.url) && !img.isDeleted && img.folder === "print"
    ).length;
    if (webCount !== printCount) {
      showToast(
        "error",
        "The number of uploaded images in Web and Print folders must be equal"
      );
      return;
    }
    setEditConfirm(true);
  };

  // ─── Render ───

  return (
    <>
      {!activeFolder ? (
        <div>
          <div className="flex gap-3 mb-4">
            <div
              className="cursor-pointer flex flex-col items-center gap-1.5 bg-lightergray py-3 px-6 rounded-lg hover:bg-lightgray transition-colors border border-mediumgray"
              onClick={() => setActiveFolder("web")}
            >
              <FiFolderPlus className="w-8 h-8 text-darkergray" />
              <p className="text-xs font-semibold text-black">Web</p>
            </div>
            <div
              className="cursor-pointer flex flex-col items-center gap-1.5 bg-lightergray py-3 px-6 rounded-lg hover:bg-lightgray transition-colors border border-mediumgray"
              onClick={() => setActiveFolder("print")}
            >
              <FiFolderPlus className="w-8 h-8 text-darkergray" />
              <p className="text-xs font-semibold text-black">Print</p>
            </div>
          </div>

          <div className="flex justify-end">
            {isUploading ? (
              <div className="black-button flex items-center justify-center px-4 py-2">
                <i className="fa-solid fa-spinner animate-spin mr-2" />{" "}
                Uploading
              </div>
            ) : (
              <Button
                onClick={handleValidateAndSave}
                className="black-button flex"
                disabled={!folderStatus.web || !folderStatus.print}
              >
                Save
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setActiveFolder(null)}
                className="gray-button flex items-center"
              >
                <FaArrowLeft className="mr-1.5" /> Back
              </Button>
              <span className="text-xs text-darkergray font-medium flex items-center">
                <HiChevronRight className="mx-1 w-3 h-3" /> {activeFolder}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                id="admin-file-upload"
                accept="image/*"
              />
              <label
                htmlFor="admin-file-upload"
                className="flex items-center black-button cursor-pointer"
              >
                <FaFileUpload className="mr-1.5" /> Upload
              </label>
              {currentFolderImages.length > 0 && (
                <Button className="outline-button" onClick={deleteAllImages}>
                  Delete All
                </Button>
              )}
              {visibleImages.length > 0 && (
                <Button
                  onClick={() =>
                    queueDownload(
                      visibleImages,
                      `${activeFolder}-${address}-admin-extras`
                    )
                  }
                  className="black-button flex items-center"
                  disabled={isDownloading}
                >
                  {isDownloading ? "Downloading..." : "Download"}
                </Button>
              )}
            </div>
          </div>

          <div className="grid gap-3 mb-4 grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {currentFolderImages.map((image, index) =>
              image.isDeleted ? (
                <div
                  key={image.id}
                  className="flex flex-col items-center justify-center border border-darkgray border-dashed h-24 relative rounded-md"
                >
                  <input
                    type="file"
                    onChange={(e) => handleFileUpload(e, image.id)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    accept="image/*"
                  />
                  <FaPlus className="text-darkergray" />
                </div>
              ) : (
                <ImageContainer
                  key={image.id}
                  image={image}
                  onDelete={(id) => deleteImage(id, image.folder)}
                  onPreview={() => setLightboxIndex(index)}
                  viewMode="grid"
                  progress={uploadProgress[image.id] || 0}
                  showDelete={true}
                  showDownload={false}
                  showTypeSelect={false}
                  eventId={null}
                  propertyId={propertyId}
                  revisionType="photo"
                />
              )
            )}
          </div>
        </div>
      )}

      <DownloadProgressToast downloads={downloads} onRemove={removeDownload} />

      <Lightbox
        images={visibleImages}
        currentIndex={lightboxIndex}
        isOpen={lightboxIndex >= 0}
        onClose={() => setLightboxIndex(-1)}
      />

      <Modal
        isOpen={editConfirm}
        onClose={() => setEditConfirm(false)}
        title="Save Confirmation"
      >
        <div className="flex flex-col space-y-4">
          <p>Are you sure you want to proceed with saving uploads?</p>
          <div className="w-full flex gap-2 justify-end mt-4">
            <Button
              onClick={() => setEditConfirm(false)}
              className="gray-button"
            >
              No
            </Button>
            <Button
              onClick={sendToReview}
              className="black-button"
              loading={reviewLoading}
            >
              Yes
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════
//  UPLOAD ADMIN PAGE
// ═══════════════════════════════════════════════════════════════════

const UploadAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const { propertyId } = useParams();

  useEffect(() => {
    if (!propertyId) return;
    const fetchMarketing = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/admin/prepareMarketing/${propertyId}`);
        // A rental's files arrive as Media rows (utils/mediaEditorView.js).
        if (res.data.success) setMarketing(editorViewOf(res.data.data));
      } catch (error) {
        console.error("Error fetching marketing:", error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchMarketing();
  }, [propertyId]);

  if (loading) return <PageLoader />;

  return (
    <div className="container">
      <p className="mb-4 text-center text-sm font-medium text-darkergray">
        {marketing?.propertyId?.address}
      </p>

      <div className="bg-white border border-mediumgray rounded-lg overflow-hidden">
        <div className="px-4 pt-3 pb-2">
          <h4 className="text-[13px] font-semibold text-black m-0 leading-tight">
            Admin Extras
          </h4>
        </div>
        <div className="px-4 pb-4 pt-2 border-t border-mediumgray">
          <AdminImageUpload
            address={marketing?.propertyId?.address}
            propertyId={marketing?.propertyId?._id || propertyId}
            adminWebPhotos={
              marketing?.adminWebPhotos?.map((photo) => ({
                ...photo,
                folder: "web",
              })) || []
            }
            adminPrintPhotos={
              marketing?.adminPrintPhotos?.map((photo) => ({
                ...photo,
                folder: "print",
              })) || []
            }
          />
        </div>
      </div>
    </div>
  );
};

export default UploadAdmin;
